import { Suspense, lazy } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";

// Admin pages — no auth wrappers; Cloudflare Access protects this domain at the edge
const Dashboard          = lazy(() => import('./pages/admin/Dashboard'));
const AdminEvents        = lazy(() => import('./pages/admin/events/AdminEvents'));
const EventForm          = lazy(() => import('./pages/admin/events/EventForm'));
const EventCategories    = lazy(() => import('./pages/admin/events/EventCategories'));
const Venues             = lazy(() => import('./pages/admin/events/Venues'));
const ScheduleTemplates  = lazy(() => import('./pages/admin/events/ScheduleTemplates'));
const AdminSponsors      = lazy(() => import('./pages/admin/sponsors/AdminSponsors'));
const AdminTeams         = lazy(() => import('./pages/admin/teams/AdminTeams'));
const TeamForm           = lazy(() => import('./pages/admin/teams/TeamForm'));
const SiteSettings       = lazy(() => import('./pages/admin/SiteSettings'));
const AdminForms         = lazy(() => import('./pages/admin/forms/AdminForms'));
const FormBuilder        = lazy(() => import('./pages/admin/forms/FormBuilder'));
const FormSubmissions    = lazy(() => import('./pages/admin/forms/FormSubmissions'));
const FormAnalytics      = lazy(() => import('./pages/admin/forms/FormAnalytics'));
const ContactSubmissions = lazy(() => import('./pages/admin/forms/ContactSubmissions'));
const AdminHelp          = lazy(() => import('./pages/admin/AdminHelp'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const AdminApp = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ErrorBoundary>
          <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Loading…</div>}>
            <Routes>
              <Route path="/"                                  element={<Navigate to="/admin" replace />} />
              <Route path="/admin"                             element={<Dashboard />} />
              <Route path="/admin/events"                      element={<AdminEvents />} />
              <Route path="/admin/events/new"                  element={<EventForm />} />
              <Route path="/admin/events/:id/edit"             element={<EventForm />} />
              <Route path="/admin/events/categories"           element={<EventCategories />} />
              <Route path="/admin/events/venues"               element={<Venues />} />
              <Route path="/admin/events/schedules"            element={<ScheduleTemplates />} />
              <Route path="/admin/sponsors"                    element={<AdminSponsors />} />
              <Route path="/admin/teams"                       element={<AdminTeams />} />
              <Route path="/admin/teams/new"                   element={<TeamForm />} />
              <Route path="/admin/teams/:id/edit"              element={<TeamForm />} />
              <Route path="/admin/settings"                    element={<SiteSettings />} />
              <Route path="/admin/forms"                       element={<AdminForms />} />
              <Route path="/admin/forms/new"                   element={<FormBuilder />} />
              <Route path="/admin/forms/:id/edit"              element={<FormBuilder />} />
              <Route path="/admin/forms/:id/submissions"       element={<FormSubmissions />} />
              <Route path="/admin/forms/:id/analytics"         element={<FormAnalytics />} />
              <Route path="/admin/forms/contact-submissions"   element={<ContactSubmissions />} />
              <Route path="/admin/help"                        element={<AdminHelp />} />
              <Route path="*"                                  element={<Navigate to="/admin" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default AdminApp;
